import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, GraduationCap, Save, Paperclip, Upload } from "lucide-react";
import dayjs from "dayjs";
import { Select } from "antd";

import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { useTrainingPermissions } from "../../hooks/useTrainingPermissions";
import {
    TRAINING_TASKS,
    TRAINING_MY_TASKS,
    TRAINING_MY_TASK_STATUS,
    TRAINING_ASSIGNMENT,
    TRAINING_VERIFY,
    TRAINING_AUDIT,
    TRAINING_MATERIALS,
    TRAINING_EVIDENCE,
} from "../../constants/api";

const statusStyles = {
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    overdue: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    // Submitted is deliberately neutral-positive: the employee is done, the
    // wait is on the verifier, so it must not read as a failure state.
    submitted: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    rejected: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};
const statusLabels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    overdue: "Overdue",
    submitted: "Awaiting Verification",
    rejected: "Sent Back",
};
const priorityStyles = {
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const HeaderCard = ({ title, priority, due_date, due_time }) => (
    <div className="bg-card border border-border rounded-lg p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
            <GraduationCap className="h-6 w-6 text-sidebar-primary flex-shrink-0" />
            <div className="min-w-0">
                <h2 className="text-lg font-semibold truncate">{title}</h2>
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                    <CalendarClock className="h-4 w-4" />
                    <span>Due {due_date ? dayjs(due_date).format("DD-MM-YYYY") : "-"}{due_time ? ` ${due_time}` : ""}</span>
                </div>
            </div>
        </div>
        <span className={cn("px-3 py-1 text-xs font-medium rounded-full capitalize", priorityStyles[priority] || priorityStyles.medium)}>
            {priority} priority
        </span>
    </div>
);

const TrainingDetail = ({ mode = "assignment" }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, update, create, remove } = useApiMethods();

    const { canAssignTraining } = useTrainingPermissions();
    const myEmployeeId = sessionStorage.getItem("employee_id");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [task, setTask] = useState(null);
    const [assignment, setAssignment] = useState(null);
    const [employeeStatus, setEmployeeStatus] = useState("");
    const [employeeRemarks, setEmployeeRemarks] = useState("");
    const [adminEdits, setAdminEdits] = useState({});
    const [auditTrail, setAuditTrail] = useState([]);
    const [verifyComment, setVerifyComment] = useState("");
    const [materials, setMaterials] = useState([]);
    const [evidence, setEvidence] = useState([]);
    const [uploading, setUploading] = useState(false);

    const loadAssignment = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(TRAINING_MY_TASKS, { id });
            const data = res?.data;
            if (data) {
                setAssignment(data);
                // 'overdue' and 'rejected' both mean "still owed", so the
                // dropdown opens on the status the employee can actually act
                // from rather than one the API would reject.
                setEmployeeStatus(
                    ["overdue", "rejected"].includes(data.status) ? "in_progress" : data.status
                );
                setEmployeeRemarks(data.employee_remarks || "");
                try {
                    const hist = await fetch(TRAINING_AUDIT, { id });
                    setAuditTrail(hist?.data || []);
                } catch (_) { /* history is non-fatal */ }
                try {
                    const mats = await fetch(TRAINING_MATERIALS, { task_id: data.task_id });
                    setMaterials(mats?.data || []);
                } catch (_) { /* materials are non-fatal */ }
                try {
                    const ev = await fetch(TRAINING_EVIDENCE, { assignment_id: id });
                    setEvidence(ev?.data || []);
                } catch (_) { /* evidence is non-fatal */ }
            }
        } catch (_) {
            ToastMsg("error", "Failed to load training");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleVerify = async (decision) => {
        if (decision === "reject" && !verifyComment.trim()) {
            ToastMsg("error", "Please give a reason before sending it back");
            return;
        }
        setSaving(true);
        try {
            const res = await update(TRAINING_VERIFY, {
                id: assignment.id,
                decision,
                comment: verifyComment.trim(),
            });
            if (res?.status === "success") {
                setVerifyComment("");
                await loadAssignment();
            }
        } finally {
            setSaving(false);
        }
    };

    const handleMaterialUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("task_id", id);
            form.append("title", file.name);
            form.append("material_type", "file");
            form.append("file", file);
            const res = await create(TRAINING_MATERIALS, form, true);
            if (res?.status === "success") await loadTask();
        } finally {
            setUploading(false);
        }
    };

    const handleMaterialLink = async () => {
        const url = window.prompt("Paste the link to the material");
        if (!url) return;
        const title = window.prompt("Give it a name", url) || url;
        const res = await create(TRAINING_MATERIALS, {
            task_id: id, title, material_type: "link", link_url: url,
        });
        if (res?.status === "success") await loadTask();
    };

    const handleMaterialRemove = async (materialId) => {
        const res = await remove(`${TRAINING_MATERIALS}?id=${materialId}`);
        if (res?.status === "success") await loadTask();
    };

    const finishing = assignment
        ? (assignment.requires_verification ? "submitted" : "completed")
        : null;
    const needsProofHint = Boolean(
        assignment
        && employeeStatus === finishing
        && assignment.status !== finishing
        && evidence.length === 0
    );

    const handleEvidenceUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("assignment_id", assignment.id);
            form.append("file", file);
            const res = await create(TRAINING_EVIDENCE, form, true);
            if (res?.status === "success") await loadAssignment();
        } finally {
            setUploading(false);
        }
    };

    const handleEvidenceRemove = async (evidenceId) => {
        const res = await remove(`${TRAINING_EVIDENCE}?id=${evidenceId}`);
        if (res?.status === "success") await loadAssignment();
    };

    const loadTask = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(TRAINING_TASKS, { id });
            if (res?.data) {
                setTask(res.data);
                try {
                    const mats = await fetch(TRAINING_MATERIALS, { task_id: id });
                    setMaterials(mats?.data || []);
                } catch (_) { /* materials are non-fatal */ }
            }
        } catch (_) {
            ToastMsg("error", "Failed to load training");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (mode === "task" && !canAssignTraining) {
            navigate("/training", { replace: true });
            return;
        }
        if (mode === "task") loadTask();
        else loadAssignment();
    }, [mode, loadTask, loadAssignment, canAssignTraining, navigate]);

    const handleEmployeeSave = async () => {
        if (!employeeStatus) {
            ToastMsg("error", "Please choose a status");
            return;
        }
        setSaving(true);
        try {
            const res = await update(TRAINING_MY_TASK_STATUS, {
                id: assignment.id,
                status: employeeStatus,
                remarks: employeeRemarks,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Status updated");
                loadAssignment();
            } else {
                ToastMsg("error", res?.message || "Failed to update");
            }
        } catch (_) {
            ToastMsg("error", "Failed to update");
        } finally {
            setSaving(false);
        }
    };

    const handleAdminOverride = async (assignmentId) => {
        const payload = { id: assignmentId, ...adminEdits[assignmentId] };
        setSaving(true);
        try {
            const res = await update(TRAINING_ASSIGNMENT, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Assignment updated");
                setAdminEdits((prev) => ({ ...prev, [assignmentId]: {} }));
                loadTask();
            } else {
                ToastMsg("error", res?.message || "Failed to update");
            }
        } catch (_) {
            ToastMsg("error", "Failed to update");
        } finally {
            setSaving(false);
        }
    };

    if (loading && !assignment && !task) {
        return <ContainerCard scrollable><div className="p-6 text-muted-foreground">Loading...</div></ContainerCard>;
    }

    return (
        <ContainerCard scrollable>
            <div className="p-3">
                <Button size="sm" variant="ghost" onClick={() => navigate(-1)} className="mb-3">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>

                {mode === "assignment" && assignment && (
                    <>
                        <HeaderCard title={assignment.title} priority={assignment.priority} due_date={assignment.due_date} due_time={assignment.due_time} />

                        <div className="bg-card border border-border rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold">Description</h3>
                                <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", statusStyles[assignment.effective_status])}>
                                    {statusLabels[assignment.effective_status]}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground whitespace-pre-line">
                                {assignment.description || "No description provided."}
                            </p>
                            {assignment.instructions && (
                                <>
                                    <h4 className="font-semibold mt-4 mb-1">Instructions</h4>
                                    <p className="text-sm text-muted-foreground whitespace-pre-line">{assignment.instructions}</p>
                                </>
                            )}
                        </div>

                        {String(assignment.employee_id) === String(myEmployeeId) && (
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="font-semibold mb-3">Update Your Status</h3>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Select
                                    size="large"
                                    value={employeeStatus || undefined}
                                    onChange={setEmployeeStatus}
                                    options={[
                                        { value: "pending", label: "Pending" },
                                        { value: "in_progress", label: "In Progress" },
                                        // The task decides what "finished" means:
                                        // submit for review, or complete outright.
                                        assignment.requires_verification
                                            ? { value: "submitted", label: "Submit for Verification" }
                                            : { value: "completed", label: "Completed" },
                                    ]}
                                    style={{ minWidth: 220 }}
                                    placeholder="Select status"
                                />
                                <Textarea
                                    value={employeeRemarks}
                                    onChange={(e) => setEmployeeRemarks(e.target.value)}
                                    placeholder="Optional remarks..."
                                    rows={2}
                                    className="flex-1"
                                />
                                <Button onClick={handleEmployeeSave} disabled={saving} className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary">
                                    <Save className="h-4 w-4 mr-1" /> Save
                                </Button>
                            </div>
                            {assignment.status === "rejected" && assignment.rejection_reason && (
                                <p className="text-sm text-orange-600 dark:text-orange-400 mt-3">
                                    Sent back: {assignment.rejection_reason}
                                </p>
                            )}
                        </div>
                        )}

                        {/* Sits directly under the status panel on purpose: an
                            employee marking a training finished should see the
                            proof field in the same breath, not several panels
                            further down. Still never required -- the API does not
                            check for evidence, and this must not imply it does. */}
                        {String(assignment.employee_id) === String(myEmployeeId) && (
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="font-semibold mb-1">Proof of completion</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Optional. Attach a certificate, photo or PDF if this training needs evidence.
                            </p>

                            {needsProofHint && (
                                <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                                    Nothing attached yet. If this training needs proof, add it before you save.
                                </p>
                            )}

                            <ul className="space-y-2 mb-3">
                                {evidence.map((e) => (
                                    <li key={e.id} className="flex items-center gap-2 text-sm">
                                        <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <a href={e.file_url} target="_blank" rel="noreferrer"
                                            className="text-blue-600 hover:underline dark:text-blue-400 break-all">
                                            {e.original_name}
                                        </a>
                                        {assignment.status !== "completed" && (
                                            <Button size="sm" variant="ghost"
                                                className="text-red-500 hover:text-red-600 shrink-0"
                                                onClick={() => handleEvidenceRemove(e.id)}>
                                                Remove
                                            </Button>
                                        )}
                                    </li>
                                ))}
                                {evidence.length === 0 && (
                                    <li className="text-sm text-muted-foreground">Nothing attached.</li>
                                )}
                            </ul>

                            {assignment.status !== "completed" ? (
                                <>
                                    <label htmlFor="evidence-file"
                                        className={cn(
                                            "inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium",
                                            "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50",
                                            uploading ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-sidebar-primary"
                                        )}>
                                        <Upload className="h-4 w-4" />
                                        {uploading ? "Uploading..." : "Attach a file"}
                                    </label>
                                    <input
                                        id="evidence-file"
                                        type="file"
                                        className="hidden"
                                        disabled={uploading}
                                        // Mirrors ALLOWED_ATTACHMENT_EXTENSIONS on the API, so the
                                        // picker offers what will actually be accepted instead of
                                        // rejecting the file after the employee chose it.
                                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                                        onChange={(e) => {
                                            handleEvidenceUpload(e.target.files?.[0]);
                                            e.target.value = "";
                                        }}
                                    />
                                    <p className="text-xs text-muted-foreground mt-2">
                                        PDF, image or document, up to 5 MB.
                                    </p>
                                </>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    This training is complete, so its evidence can no longer be changed.
                                </p>
                            )}
                        </div>
                        )}


                        {/* Verifier panel. Shown only for work awaiting review and
                            never to the assignee -- the API enforces the same rule,
                            this just avoids offering an action that would 403. */}
                        {assignment.status === "submitted"
                            && String(assignment.employee_id) !== String(myEmployeeId)
                            && canAssignTraining && (
                            <div className="bg-card border border-border rounded-lg p-4">
                                <h3 className="font-semibold mb-3">Verify Submission</h3>
                                <Textarea
                                    value={verifyComment}
                                    onChange={(e) => setVerifyComment(e.target.value)}
                                    placeholder="Comment (required when sending back)..."
                                    rows={2}
                                />
                                <div className="flex gap-2 mt-3">
                                    <Button onClick={() => handleVerify("approve")} disabled={saving}
                                        className="bg-green-600 text-white hover:bg-green-700">
                                        Approve
                                    </Button>
                                    <Button onClick={() => handleVerify("reject")} disabled={saving} variant="outline">
                                        Send Back
                                    </Button>
                                </div>
                            </div>
                        )}

                        {materials.length > 0 && (
                            <div className="bg-card border border-border rounded-lg p-4">
                                <h3 className="font-semibold mb-3">Course Material</h3>
                                <ul className="space-y-2">
                                    {materials.map((m) => (
                                        <li key={m.id} className="flex items-center gap-2 text-sm">
                                            <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                                            <a
                                                href={m.material_type === "file" ? m.file_url : m.link_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline dark:text-blue-400"
                                            >
                                                {m.title}
                                            </a>
                                            <span className="text-xs text-muted-foreground">
                                                ({m.material_type})
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {auditTrail.length > 0 && (
                            <div className="bg-card border border-border rounded-lg p-4">
                                <h3 className="font-semibold mb-3">History</h3>
                                <ol className="space-y-2">
                                    {auditTrail.map((e) => (
                                        <li key={e.id} className="text-sm flex flex-wrap gap-x-2 border-b border-border/50 pb-2 last:border-0">
                                            <span className="font-medium">{statusLabels[e.to_status] || e.action}</span>
                                            <span className="text-muted-foreground">by {e.performed_by}</span>
                                            <span className="text-muted-foreground">
                                                {e.created_date ? dayjs(e.created_date).format("DD MMM YYYY, HH:mm") : ""}
                                            </span>
                                            {e.cycle > 1 && (
                                                <span className="text-xs px-1.5 rounded bg-muted">cycle {e.cycle}</span>
                                            )}
                                            {e.comment && <span className="w-full text-muted-foreground">{e.comment}</span>}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                    </>
                )}

                {mode === "task" && task && (
                    <>
                        <HeaderCard title={task.title} priority={task.priority} due_date={task.due_date} due_time={task.due_time} />

                        <div className="bg-card border border-border rounded-lg p-4 mb-4">
                            <h3 className="font-semibold mb-1">Course Material</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Files and links every assignee can open from their training.
                            </p>
                            <ul className="space-y-2 mb-3">
                                {materials.map((m) => (
                                    <li key={m.id} className="flex items-center gap-2 text-sm">
                                        <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <a href={m.material_type === "file" ? m.file_url : m.link_url}
                                            target="_blank" rel="noreferrer"
                                            className="text-blue-600 hover:underline dark:text-blue-400">
                                            {m.title}
                                        </a>
                                        <span className="text-xs text-muted-foreground">({m.material_type})</span>
                                        <Button size="sm" variant="ghost"
                                            className="text-red-500 hover:text-red-600"
                                            onClick={() => handleMaterialRemove(m.id)}>
                                            Remove
                                        </Button>
                                    </li>
                                ))}
                                {materials.length === 0 && (
                                    <li className="text-sm text-muted-foreground">Nothing added yet.</li>
                                )}
                            </ul>
                            <div className="flex items-center gap-3">
                                <input type="file" disabled={uploading}
                                    onChange={(e) => { handleMaterialUpload(e.target.files?.[0]); e.target.value = ""; }}
                                    className="text-sm" />
                                <Button size="sm" variant="outline" onClick={handleMaterialLink}>
                                    Add link
                                </Button>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-4 mb-4">
                            <h3 className="font-semibold mb-2">Description</h3>
                            <p className="text-sm text-muted-foreground whitespace-pre-line">{task.description || "-"}</p>
                            {task.instructions && (
                                <>
                                    <h4 className="font-semibold mt-4 mb-1">Instructions</h4>
                                    <p className="text-sm text-muted-foreground whitespace-pre-line">{task.instructions}</p>
                                </>
                            )}
                            <div className="mt-3 text-xs text-muted-foreground">
                                Progress: {task.completed_count}/{task.total_assignees} ({task.completion_percent}%)
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold">Assignees</h3>
                                {canAssignTraining && (
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/training/edit/${task.id}`)}>
                                        Edit Training
                                    </Button>
                                )}
                            </div>
                            <div className="max-h-[min(480px,calc(100vh-360px))] overflow-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40">
                                        <tr className="text-left text-muted-foreground">
                                            <th className="px-3 py-2 font-medium">Employee</th>
                                            <th className="px-3 py-2 font-medium">Department</th>
                                            <th className="px-3 py-2 font-medium">Status</th>
                                            <th className="px-3 py-2 font-medium">Employee Remarks</th>
                                            {canAssignTraining && <th className="px-3 py-2 font-medium">Admin Override</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(task.assignments || []).map((a) => {
                                            const edit = adminEdits[a.id] || {};
                                            return (
                                                <tr key={a.id} className="border-t border-border align-top">
                                                    <td className="px-3 py-2">{a.employee_name}</td>
                                                    <td className="px-3 py-2">{a.employee_department || "-"}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", statusStyles[a.effective_status])}>
                                                            {statusLabels[a.effective_status]}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 max-w-xs">
                                                        <p className="text-xs text-muted-foreground line-clamp-2">{a.employee_remarks || "-"}</p>
                                                    </td>
                                                    {canAssignTraining && (
                                                        <td className="px-3 py-2">
                                                            <div className="flex flex-col gap-2">
                                                                <Select
                                                                    size="large"
                                                                    value={edit.status ?? a.status}
                                                                    onChange={(v) => setAdminEdits((p) => ({ ...p, [a.id]: { ...p[a.id], status: v } }))}
                                                                    options={[
                                                                        { value: "pending", label: "Pending" },
                                                                        { value: "in_progress", label: "In Progress" },
                                                                        { value: "completed", label: "Completed" },
                                                                        { value: "overdue", label: "Overdue" },
                                                                    ]}
                                                                    style={{ minWidth: 140 }}
                                                                    placeholder="Select status"
                                                                />
                                                                <Textarea
                                                                    value={edit.admin_remarks ?? (a.admin_remarks || "")}
                                                                    onChange={(e) => setAdminEdits((p) => ({ ...p, [a.id]: { ...p[a.id], admin_remarks: e.target.value } }))}
                                                                    placeholder="Admin remarks..."
                                                                    rows={2}
                                                                    className="text-xs"
                                                                />
                                                                <Button size="sm" onClick={() => handleAdminOverride(a.id)} disabled={saving} className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary">
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </ContainerCard>
    );
};

export default TrainingDetail;
